import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Stack} from '@mui/material';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useObservable} from '@ngneat/react-rxjs';
import {useParams} from 'react-router-dom';
import {emitOnce} from '@ngneat/elf';

import {searchClientsContractsEffect} from '@store/clients/contracts/clients-contracts.effects';
import {ClientsContract, clientsContractsQuery, clientsContractsService} from '@store/clients/contracts';
import {ManageClientService} from '@store/clients/services';

import ModalComponent from '@components/modal/Modal.component';
import SearchComponent from '@components/inputs/search/Search.component';
import ManageContractDrawer from '@screens/admin/clients/details/contracts/manageContract/ManageContract.drawer';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import NewTableComponent from '@components/table/NewTable.component';
import ContractServicesModal
  from '@screens/admin/clients/details/contractedServices/manageService/steps/ContractServices.modal';

import COLUMN_TYPES from '@utils/table/columnTypes.table';
import I18n from '@utils/i18n.utils';

interface SelectContractModalProps {
  handleClose: () => void;
  handlePrevious: () => void;
  handleValidate: (contract: ClientsContract) => void;
  service: ManageClientService;
}

const SelectContractModal = (props: SelectContractModalProps) => {
  const {handleClose, handlePrevious, handleValidate, service} = props;

  const {t} = useTranslation();
  const {clientId} = useParams();

  const [showAddContract, setShowAddContract] = useState(false);

  const searchClientsContracts = useEffectFn(searchClientsContractsEffect);

  const [{clientsContracts, loading}] = useObservable(clientsContractsQuery.clientsContracts$);
  const [showContractServices, setShowContractServices] = useState<ClientsContract | undefined>(undefined);

  const [selectedClientContract, setSelectedClientContract] = useState<ClientsContract | undefined>(service.contract);

  const [filters] = useObservable(clientsContractsQuery.filters$);
  const [sortModel] = useObservable(clientsContractsQuery.sort$);
  const [tablePagination] = useObservable(clientsContractsQuery.pagination$);

  useEffect(() => {
    if (clientId) clientsContractsService.resetFilters(clientId);
  }, [clientId]);

  useEffect(() => {
    searchClientsContracts({filters, tablePagination, sort: sortModel});
  }, [filters, searchClientsContracts, sortModel, tablePagination]);

  return (
    <>
      <ModalComponent
        aria-label="small-padding"
        handleClose={handleClose}
        maxWidth={false}
        title={t('clients.details.contractedServices.manage.selectContract.title')}
        content={(
          <Stack pt="20px" height="85vh" width="100%" spacing={2} overflow="hidden">
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Button variant="outlined" color="primary" onClick={() => setShowAddContract(true)}>
                {t('clients.details.contractedServices.manage.selectContract.addButton')}
              </Button>
              <TableFiltersComponent
                filters={filters}
                handleSave={clientsContractsService.setFilters('tableFilters')}
                handleReset={() => clientId ? clientsContractsService.resetFilters() : undefined}/>
            </Stack>
            <SearchComponent
              value={filters.search}
              handleChange={(value) => {
                emitOnce(() => {
                  clientsContractsService.setFilters('search')(value);
                  clientsContractsService.setPagination('page')(1);
                });
              }}/>
            <NewTableComponent
              loading={loading}
              columns={[
                COLUMN_TYPES.TEXT_BOLD({
                  field: 'contractReference',
                  label: t('clients.details.contractedServices.manage.selectContract.table.reference'),
                  width: 150
                }),
                COLUMN_TYPES.TEXT_BOLD({
                  field: 'contractName',
                  label: t('clients.details.contractedServices.manage.selectContract.table.name'),
                  fullWidth: true,
                  minWidth: 250
                }),
                COLUMN_TYPES.TEXT({
                  field: 'startDate',
                  label: t('clients.details.contractedServices.manage.selectContract.table.startDate'),
                  valueFormatter: (params) => params.value ? I18n.t('global.date', {date: new Date(params.value)}) : '',
                  width: 140
                }),
                COLUMN_TYPES.TEXT({
                  field: 'endDate',
                  label: t('clients.details.contractedServices.manage.selectContract.table.endDate'),
                  valueFormatter: (params) => params.value ? I18n.t('global.date', {date: new Date(params.value)}) : '',
                  width: 130
                }),
                COLUMN_TYPES.TEXT({
                  field: 'clientOrganisationUnitName',
                  label: t('clients.details.contractedServices.manage.selectContract.table.clientUnit'),
                  fullWidth: true,
                  minWidth: 350
                }),
                COLUMN_TYPES.TEXT_WITH_ACTION({
                  field: 'servicesCount',
                  actionLabel: (contract: ClientsContract) => contract.servicesCount,
                  handleClick: (contract: ClientsContract) => setShowContractServices(contract),
                  label: t('clients.details.contractedServices.manage.selectContract.table.servicesCount'),
                  width: 100
                }),
              ]}
              handlePageSizeChange={clientsContractsService.setPagination('limit')}
              pageSize={tablePagination.limit}
              handlePageChange={(page) => clientsContractsService.setPagination('page')(page + 1)}
              page={tablePagination.page - 1}
              handleSortModelChange={clientsContractsService.setSort}
              sortModel={[sortModel]}
              singleRowSelection
              handleRowSelected={(selected) => {
                const clientContract = selected.length ? clientsContracts.find((c) => c.id === selected[0]) : undefined;
                setSelectedClientContract(clientContract);
              }}
              rowsSelected={selectedClientContract ? [selectedClientContract.id] : []}
              totalRows={clientsContractsQuery.totalRows}
              data={clientsContracts}/>
          </Stack>
        )}
        actions={(
          <>
            <Button variant="outlined" color="primary" onClick={handlePrevious}>
              {t('global.previous')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!selectedClientContract}
              onClick={() => selectedClientContract ? handleValidate(selectedClientContract) : undefined}>
              {t('global.validate')}
            </Button>
          </>
        )}/>
      {showAddContract && (
        <ManageContractDrawer
          handleClose={() => setShowAddContract(false)}
          handleCreated={(contract) => setSelectedClientContract(contract)}
          isCreation/>
      )}
      {!!showContractServices && (
        <ContractServicesModal
          contract={showContractServices}
          contractId={showContractServices.id}
          handleClose={() => setShowContractServices(undefined)}/>
      )}
    </>
  );
};

export default SelectContractModal;
